/**
 * Theme toggle: switches between light and dark, persists in localStorage,
 * and updates the header icon (sun = switch to light, moon = switch to dark).
 */

const THEME_KEY = "mecs-theme";
const THEME_DARK = "dark";
const THEME_LIGHT = "light";
const THEME_BTN_SELECTOR = ".site-header__icon-btn--theme";

/** @type {HTMLElement | null} */
let themeButtonRef = null;

function isValidTheme(value) {
  return value === THEME_LIGHT || value === THEME_DARK;
}

function getSystemPreference() {
  if (typeof window.matchMedia === "undefined") return THEME_DARK;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? THEME_LIGHT : THEME_DARK;
}

function getStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return isValidTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

function getCurrentTheme() {
  return getStoredTheme() ?? getSystemPreference();
}

function applyTheme(theme) {
  if (!isValidTheme(theme)) return;

  document.documentElement.setAttribute("data-theme", theme);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch (_) {}
  updateThemeIcon(theme);
}

function updateThemeIcon(theme) {
  const btn = themeButtonRef ?? document.querySelector(THEME_BTN_SELECTOR);
  if (!btn) return;
  if (!themeButtonRef) themeButtonRef = btn;

  const sunIcon = btn.querySelector("[data-theme-icon='sun']");
  const moonIcon = btn.querySelector("[data-theme-icon='moon']");

  if (sunIcon && moonIcon) {
    const isDark = theme === THEME_DARK;
    sunIcon.hidden = !isDark;
    moonIcon.hidden = isDark;
  }

  btn.setAttribute(
    "aria-label",
    theme === THEME_DARK ? "Switch to light theme" : "Switch to dark theme"
  );
}

function toggleTheme() {
  // Use DOM state so we always toggle from what's actually applied (avoids desync with localStorage)
  const applied = document.documentElement.getAttribute("data-theme");
  const current = isValidTheme(applied) ? applied : getCurrentTheme();
  applyTheme(current === THEME_DARK ? THEME_LIGHT : THEME_DARK);
}

export function initThemeToggle() {
  themeButtonRef = document.querySelector(THEME_BTN_SELECTOR);
  const theme = getCurrentTheme();
  applyTheme(theme);

  if (themeButtonRef) {
    themeButtonRef.addEventListener("click", toggleTheme);
  }

  try {
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    mq.addEventListener("change", () => {
      if (getStoredTheme() === null) applyTheme(getSystemPreference());
    });
  } catch (_) {}
}
